/** Branch-supported broadleaf crowns. One seeded botanical layout feeds both
 * geometry levels; season changes its foliage, never its woody skeleton. */
import * as THREE from "three";
import { range, type Rng } from "@hyperlight/shared";
import type { BuildCtx, C3, SpeciesParams } from "./types";
import { V, shade } from "./primitives";
import { TREE_STEM, NO_JOINT, windJoint, type WindJoint } from './treeWind';

interface Knot { p: THREE.Vector3; radius: number }
interface Cluster {
  center: THREE.Vector3;
  radius: number;
  stretch: THREE.Vector3;
  phase: number;
  lobes: number[];
  tone: number;
}
interface Limb { path: Knot[]; twigs: Knot[][]; clusters: Cluster[] }
export interface BroadleafPlan { stem: Knot[]; limbs: Limb[]; radius: number; height: number; rootPhase: number }

/** Geometry decisions only: no pigment/season/LOD dependent random draws. */
export function planBroadleaf(rng: Rng, sp: SpeciesParams, maturity: number): BroadleafPlan {
  const grow = .45 + .75 * maturity, girth = .65 + .6 * maturity;
  const h = range(rng, ...sp.trunk.height) * grow, r = sp.trunk.radius * girth;
  const profile = sp.branching ?? { spread: .32, rise: .35, leafScale: .62 };
  const lean = h * sp.trunk.lean;
  const top = V(range(rng, -.8, .8) * lean, h * sp.trunk.forkHeight, range(rng, -.8, .8) * lean);
  const stem: Knot[] = [
    { p: V(0, 0, 0), radius: r },
    { p: V(top.x * .12, top.y * .38, top.z * .12), radius: r * .86 },
    { p: V(top.x * .52, top.y * .73, top.z * .52), radius: r * .65 },
    { p: top, radius: r * .43 },
  ];
  const limbs: Limb[] = [];
  const branches = Math.round(range(rng, ...sp.trunk.forks));
  const phase = rng() * Math.PI * 2;
  for (let i = 0; i <= branches; i++) {
    const leader = i === branches;
    const angle = phase + i * 2.399963 + range(rng, -.25, .25);
    const outward = V(Math.cos(angle), 0, Math.sin(angle));
    const across = V(-outward.z, 0, outward.x);
    const attach = leader ? 1 : range(rng, .54, .94);
    const segment = attach < .73 ? 1 : 2;
    const start = stem[segment].p.clone().lerp(stem[segment + 1].p,
      (attach - (segment === 1 ? .38 : .73)) / (segment === 1 ? .35 : .27));
    const reach = h * profile.spread * (leader ? .22 : range(rng, .72, 1.12));
    const rise = h * profile.rise * (leader ? 1.25 : range(rng, .68, 1.05));
    const end = start.clone().addScaledVector(outward, reach).add(V(0, rise, 0));
    const elbow = start.clone().lerp(end, .46).addScaledVector(across, range(rng, -.15, .15) * h);
    elbow.y -= rise * .12;
    const path: Knot[] = [{ p: start, radius: r * (leader ? .43 : .48) },
      { p: elbow, radius: r * .28 }, { p: end, radius: r * .13 }];
    const twigs: Knot[][] = [], clusters: Cluster[] = [];
    const count = leader ? 2 : Math.min(3, Math.max(2, Math.round(range(rng, ...sp.canopy.count) * .65)));
    for (let j = 0; j < count; j++) {
      const size = range(rng, ...sp.canopy.size) * grow * profile.leafScale * range(rng, .85, 1.12);
      const fan = (j - (count - 1) / 2) * 1.35;
      const center = (j === 0 ? elbow.clone().lerp(end, .7) : end.clone()).addScaledVector(across, fan * size)
        .addScaledVector(outward, size * range(rng, .3, 1.0))
        .add(V(0, size * (j === 1 ? .9 : range(rng, .15, .65)), 0));
      const bud = center.clone().add(V(0, -size * .24, 0));
      const root = elbow.clone().lerp(end, .76 + .1 * j / count);
      const knuckle = root.clone().lerp(bud, .52).add(V(0, -size * .13, 0));
      twigs.push([{ p: root, radius: r * .105 }, { p: knuckle, radius: r * .065 }, { p: bud, radius: r * .025 }]);
      clusters.push({ center, radius: size,
        stretch: V(range(rng, .95, 1.25), range(rng, .8, 1.15), range(rng, .9, 1.2)),
        phase: rng() * Math.PI * 2, lobes: Array.from({ length: 7 }, () => range(rng, 1 - sp.canopy.rough * .8, 1 + sp.canopy.rough * .6)),
        tone: range(rng, .86, 1.04) });
    }
    limbs.push({ path, twigs, clusters });
  }
  return { stem, limbs, radius: r, height: h, rootPhase: phase };
}

class MeshWriter {
  positions: number[] = [];
  colors: number[] = [];
  sways: number[] = [];
  branches: number[] = [];
  twigs: number[] = [];
  branch: WindJoint = TREE_STEM;
  twig: WindJoint = NO_JOINT;
  tri(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, color: C3, sway: number): void {
    for (const p of [a, b, c]) {
      this.positions.push(p.x, p.y, p.z); this.colors.push(...color); this.sways.push(sway);
      this.branches.push(...this.branch); this.twigs.push(...this.twig);
    }
  }
  /** Connected ring sweep: taper and bend without capped seams at each elbow. */
  wood(path: Knot[], color: C3, sides = 5, capRoot = true): void {
    const rings = path.map((k, i) => {
      const direction = path[Math.min(i + 1, path.length - 1)].p.clone().sub(path[Math.max(0, i - 1)].p).normalize();
      const q = new THREE.Quaternion().setFromUnitVectors(V(0, 1, 0), direction);
      return Array.from({ length: sides }, (_, j) => V(Math.cos(j * Math.PI * 2 / sides) * k.radius, 0,
        Math.sin(j * Math.PI * 2 / sides) * k.radius).applyQuaternion(q).add(k.p));
    });
    for (let i = 0; i < sides; i++) {
      const next = (i + 1) % sides;
      if (capRoot) this.tri(path[0].p, rings[0][i], rings[0][next], color, 0);
      for (let j = 0; j < path.length - 1; j++) {
        this.tri(rings[j][i], rings[j + 1][i], rings[j][next], color, 0);
        this.tri(rings[j][next], rings[j + 1][i], rings[j + 1][next], color, 0);
      }
      this.tri(path.at(-1)!.p, rings.at(-1)![next], rings.at(-1)![i], color, 0);
    }
  }
  /** Lobed closed foliage shell. Smaller overlapping clusters supply the edge
   * detail; broad facets inside each shell retain the game's material language. */
  foliage(c: Cluster, color: C3, coarse = false, sway = 1, sides = coarse ? 8 : 7): void {
    const transform = (p: THREE.Vector3) => p.multiply(c.stretch).multiplyScalar(c.radius).add(c.center);
    const rings = (coarse ? [[0, 1]] : [[-.48, .55], [0, 1], [.55, .58]]).map(([y, radius]) =>
      Array.from({ length: sides }, (_, i) => {
        const a = c.phase + i * Math.PI * 2 / sides;
        const lobe = coarse ? 1 : c.lobes[i];
        return transform(V(Math.cos(a) * radius * lobe, y + (coarse ? 0 : (i % 2 ? .08 : -.04)), Math.sin(a) * radius * lobe));
      }));
    const bottom = transform(V(-.12, -.76, .05)), top = transform(V(.1, .87, -.08));
    for (let i = 0; i < sides; i++) {
      const next = (i + 1) % sides;
      this.tri(bottom, rings[0][i], rings[0][next], color, sway);
      for (let j = 0; j < rings.length - 1; j++) {
        this.tri(rings[j][i], rings[j + 1][i], rings[j][next], color, sway);
        this.tri(rings[j][next], rings[j + 1][i], rings[j + 1][next], color, sway);
      }
      this.tri(top, rings.at(-1)![next], rings.at(-1)![i], color, sway);
    }
  }
  finish(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.positions, 3));
    g.setAttribute("color", new THREE.Float32BufferAttribute(this.colors, 3));
    g.setAttribute("sway", new THREE.Float32BufferAttribute(this.sways, 1));
    g.setAttribute('branchWind', new THREE.Float32BufferAttribute(this.branches, 4));
    g.setAttribute('twigWind', new THREE.Float32BufferAttribute(this.twigs, 4));
    g.computeVertexNormals();
    return g;
  }
}

/** Every arch starts inside the existing stem and ends below its support plane.
 * LODs share these paths; roots inherit the stiff stem's exact wind transform. */
export function planPropRoots(plan: BroadleafPlan, sp: SpeciesParams): Knot[][] {
  if (!sp.propRoots) return [];
  const { count, spread, attach } = sp.propRoots;
  return Array.from({ length: count }, (_, i) => {
    const angle = plan.rootPhase + i * 2.399963;
    const height = plan.height * attach * (.72 + .28 * (i % 3) / 2);
    const segment = Math.min(plan.stem.length - 2, Math.max(0, plan.stem.findIndex(k => k.p.y >= height) - 1));
    const a = plan.stem[segment], b = plan.stem[segment + 1];
    const start = a.p.clone().lerp(b.p, Math.min(1, (height - a.p.y) / (b.p.y - a.p.y)));
    const reach = plan.height * spread * (.78 + .22 * (i % 4) / 3);
    const outward = V(Math.cos(angle), 0, Math.sin(angle));
    const elbow = start.clone().addScaledVector(outward, reach * .48);elbow.y = height * .64;
    const heel = start.clone().addScaledVector(outward, reach * .85);heel.y = .28;
    const tip = start.clone().addScaledVector(outward, reach);tip.y = -.55;
    return [{ p: start, radius: plan.radius * .52 }, { p: elbow, radius: plan.radius * .33 },
      { p: heel, radius: plan.radius * .20 }, { p: tip, radius: plan.radius * .09 }];
  });
}

export function buildBroadleaf(rng: Rng, sp: SpeciesParams, ctx: BuildCtx, bark: C3, leaf: C3, snow: C3, far: boolean): THREE.BufferGeometry {
  const plan = planBroadleaf(rng, sp, ctx.maturity), mesh = new MeshWriter();
  const bare = sp.deciduous && ctx.season === "winter";
  mesh.wood(far ? [plan.stem[0], plan.stem.at(-1)!] : plan.stem, bark, far ? 3 : 6);
  const roots = planPropRoots(plan, sp);
  for (const root of roots) mesh.wood(root, bark, far ? 3 : 5);
  if (!far) for (let i = 0; i < roots.length; i++) {
    const tip = roots[i].at(-1)!.p;
    for (let j = 0; j < 2; j++) {
      const p = tip.clone().multiplyScalar(.66 + j * .15);p.y = -.55;
      mesh.wood([{ p, radius: plan.radius * (.17 + .05 * j) },
        { p: V(p.x + .05, .24 + .16 * ((i + j) % 3), p.z - .04), radius: .012 }], bark, 4);
    }
  }
  if (!far) {
    for (let i = 0; i < (sp.propRoots ? 0 : 4); i++) {
      const a = plan.rootPhase + i * Math.PI / 2, r = plan.radius;
      mesh.wood([{ p: V(0, r * 1.8, 0), radius: r * .45 },
        { p: V(Math.cos(a) * r * 2.5, -.08, Math.sin(a) * r * 2.5), radius: r * .08 }], bark, 4);
    }
    if (sp.motifs.barkBands) {
      for (let i = 1; i <= 5; i++) {
        const k = i / 6, segment = k < .38 ? 0 : k < .73 ? 1 : 2;
        const lo = [0, .38, .73][segment], hi = [.38, .73, 1][segment];
        const a = plan.stem[segment], b = plan.stem[segment + 1], f = (k - lo) / (hi - lo);
        const p = a.p.clone().lerp(b.p, f), r = (a.radius + (b.radius - a.radius) * f) * 1.035;
        const direction = b.p.clone().sub(a.p).normalize();
        mesh.wood([{ p, radius: r }, { p: p.clone().addScaledVector(direction, .055), radius: r * .99 }], [.16, .15, .14], 6);
      }
    }
  }
  for (const limb of plan.limbs) {
    mesh.branch = far ? TREE_STEM : windJoint(limb.path[0].p, limb.path.at(-1)!.p, limb.path[0].radius);
    mesh.twig = NO_JOINT;
    mesh.wood(far ? [limb.path[0], limb.path.at(-1)!] : limb.path, bark, far ? 3 : 5, !far);
    if (far) {
      if (bare) {
        // One terminal branch per limb retains the bare winter outline.
        const twig = limb.twigs[1];
        mesh.wood([twig[0], twig.at(-1)!], bark, 3);
      } else {
        const box = new THREE.Box3();
        for (const c of limb.clusters) {
          const ext = c.stretch.clone().multiplyScalar(c.radius * 1.2);
          box.expandByPoint(c.center.clone().sub(ext));box.expandByPoint(c.center.clone().add(ext));
        }
        const extent = box.getSize(V(0, 0, 0)).multiplyScalar(.5);
        mesh.foliage({ ...limb.clusters[0], center: box.getCenter(V(0, 0, 0)), radius: 1, stretch: extent }, leaf, true);
      }
    } else {
      for (let j = 0; j < limb.clusters.length; j++) {
        const c = limb.clusters[j];
        const twig = limb.twigs[j];
        mesh.twig = windJoint(twig[0].p, twig.at(-1)!.p, twig[0].radius);
        mesh.wood(limb.twigs[j], bark, 4);
        if (bare) {
          const end = limb.twigs[j].at(-1)!;
          mesh.wood([end, { p: end.p.clone().add(V(c.radius * .28, c.radius * .65, -c.radius * .18)), radius: end.radius * .35 }], bark, 3);
          if (j === 1) {
            // Snow here rests on the main limb, not its terminal twig.
            mesh.twig = NO_JOINT;
            mesh.foliage({ ...c, center: limb.path[1].p.clone().add(V(0, plan.radius * .35, 0)), radius: plan.radius * .55,
              stretch: V(1.5, .45, .8) }, snow, true, .15);
          }
        } else {
          mesh.foliage(c, shade(leaf, c.tone));
          // Secondary leaf fans break the larger shell's rim. Their bases
          // overlap it, so they read as one branching spray with a ragged edge.
          for (let k = 0; k < 3; k++) {
            const angle = c.phase + k * 2.399963;
            mesh.foliage({ ...c,
              center: c.center.clone().add(V(Math.cos(angle) * c.radius * .83,
                c.radius * (k === 1 ? .52 : -.12), Math.sin(angle) * c.radius * .83)),
              radius: c.radius * (.43 + c.lobes[k] * .1),
              stretch: V(c.stretch.x, c.stretch.y * .8, c.stretch.z),
            }, shade(leaf, c.tone * (k === 1 ? 1.02 : .96)), true, 1, 5);
          }
          if (sp.motifs.blossoms && sp.palette.blossom && j === 1) {
            mesh.foliage({ ...c, center: c.center.clone().add(V(c.radius * .25, c.radius * .7, 0)), radius: c.radius * .25 }, sp.palette.blossom, true);
          }
        }
      }
    }
  }
  const geo = mesh.finish();
  if (roots.length) geo.userData.propRoots = roots.map(path => path.map(k => ({ p: k.p.toArray(), radius: k.radius })));
  return geo;
}
